import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { AppError } from '../../api/contract';
import { useRepository } from '../../data/RepositoryProvider';
import { MutationError, toDialogError } from '../partners/PartnerFormDialog';
import { agentRepositoryToken } from './repository';
import type { AgentDetail } from './types';

export function DefaultUplineDialog({
  agent,
  onClose,
  onSaved,
  open,
}: {
  readonly agent: AgentDetail;
  readonly onClose: () => void;
  readonly onSaved: (agent: AgentDetail) => void;
  readonly open: boolean;
}) {
  const repository = useRepository(agentRepositoryToken);
  const { t } = useTranslation();
  const submittingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
    watch,
  } = useForm<{ readonly memberId: string; readonly reason: string }>({
    defaultValues: { memberId: agent.defaultUpline?.memberId ?? '', reason: '' },
  });
  const memberId = watch('memberId');
  const reason = watch('reason');

  useEffect(() => {
    if (!open) return;
    reset({ memberId: agent.defaultUpline?.memberId ?? '', reason: '' });
    setError(null);
    submittingRef.current = false;
    setBusy(false);
  }, [agent.defaultUpline?.memberId, open, reset]);

  const submit = handleSubmit(async (values) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await repository.setDefaultUpline(agent.id, {
        memberId: values.memberId.trim(),
        reason: values.reason.trim(),
      });
      onSaved(result.data);
      onClose();
    } catch (submitError) {
      setError(toDialogError(submitError, t('agents.errors.mutation')));
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  });

  return (
    <Dialog fullWidth onClose={() => !submittingRef.current && onClose()} open={open}>
      <DialogTitle>{t('agents.upline.title')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="agent-upline-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          {error === null ? null : <MutationError error={error} />}
          <TextField
            error={errors.memberId !== undefined}
            helperText={errors.memberId?.message}
            label={t('agents.fields.memberId')}
            required
            {...register('memberId', {
              required: t('agents.validation.memberRequired'),
              validate: (value) => value.trim().length > 0 || t('agents.validation.memberRequired'),
            })}
          />
          <TextField
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('agents.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: t('agents.validation.reasonRequired'),
              validate: (value) => value.trim().length > 0 || t('agents.validation.reasonRequired'),
            })}
          />
          <Alert severity="warning">
            <Typography>{t('agents.upline.confirmation')}</Typography>
            <Typography component="div" sx={{ overflowWrap: 'anywhere' }} variant="body2">
              {memberId.trim() || t('common.notAvailable')}
            </Typography>
            <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }} variant="body2">
              {reason.trim() || t('common.notAvailable')}
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {t('actions.cancel')}
        </Button>
        <Button form="agent-upline-form" loading={busy} type="submit" variant="contained">
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
